import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BorderRadiusSmall, ColorNeutralWhite } from 'autogen/design-tokens/tokens';
import { Basket as BasketTrans } from 'autogen/translation-keys/trans-website-minibasket';
import Icon from 'components/Icon';
import MiniBasket from 'components/MiniBasket';
import { miniBasketActiveAtom } from 'components/MiniBasket/recoil/miniBasketAtoms';
import { SessionMenuAnchorItem } from 'components/SessionMenu/components/SessionMenuItem';
import type { LinkAction } from 'components/shared/Routing/components/LinkAction/shared/types';
import Tippy from 'components/Tippy';
import dynamic from 'helpers/dynamic';
import useIsMobile from 'hooks/responsive/useIsMobile';
import { useSetRecoilState } from 'recoil';
import { breakpoint, sizes } from 'variables';
import { HeaderItemText } from './shared/styles';

const BasketQuantityBadge = dynamic(() => import('components/generic/badge/badges/BasketQuantityBadge'), {
    ssr: false
});

/* #region styles */
const StyledTippy = styled(Tippy.Lazy)`
    && {
        background-color: ${ColorNeutralWhite};
        color: initial;
        line-height: initial;
        outline: initial;
        border-radius: ${BorderRadiusSmall};
        width: 496px;

        box-shadow: 0 0 24px rgba(0, 0, 0, 0.2);

        &[data-placement^='top'] > .tippy-arrow::before {
            bottom: -8px;
            border-top-color: ${ColorNeutralWhite};
            border-width: 8px 8px 0;
        }

        &[data-placement^='bottom'] > .tippy-arrow::before {
            top: -8px;
            border-bottom-color: ${ColorNeutralWhite};
            border-width: 0 8px 8px;
        }

        &[data-placement^='left'] > .tippy-arrow::before {
            right: -8px;
            border-left-color: ${ColorNeutralWhite};
            border-width: 8px 0 8px 8px;
        }

        &[data-placement^='right'] > .tippy-arrow::before {
            left: -8px;
            border-right-color: ${ColorNeutralWhite};
            border-width: 8px 8px 8px 0;
        }
    }
`;

const StyledBasketQuantityBadge = styled(BasketQuantityBadge)`
    position: absolute;
    top: -6px;
    right: -2px;

    ${breakpoint.up(sizes.lg)} {
        right: 4px;
    }
`;
/* #endregion */

export default function Basket({ linkAction, ...restProps }: { linkAction: LinkAction }) {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const setIsMiniBasketActive = useSetRecoilState(miniBasketActiveAtom);

    return (
        <StyledTippy
            disabled={isMobile}
            interactive
            content={<MiniBasket />}
            maxWidth={496}
            delay={[null, 250]}
            placement="bottom-end"
            onShow={() => {
                setIsMiniBasketActive(true);
            }}
            onHide={() => {
                setIsMiniBasketActive(false);
            }}
        >
            <SessionMenuAnchorItem
                action={linkAction}
                {...restProps}
                aria-label={t(BasketTrans)}
                icon={<Icon type="Basket" size={16} aria-hidden />}
            >
                <StyledBasketQuantityBadge />
                <HeaderItemText>{t(BasketTrans)}</HeaderItemText>
            </SessionMenuAnchorItem>
        </StyledTippy>
    );
}
